// src/App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, storage, functions } from './firebase';
import Login from './components/Login';
import UserProfile from './components/UserProfile';
import NotePlayerPublicLink from './components/NotePlayerPublicLink';
import Notes from './components/Notes';
import Sidebar from './components/Sidebar';
import SearchModal from './components/SearchModal';
import NoteDetail from './components/NoteDetail';
import NoteDetailWindow from './components/NoteDetailWindow';
import ContactsList from './components/contacts/ContactsList';
import ContactDetail from './components/contacts/ContactDetail';
import GalleryPage from './components/GalleryPage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import FloatingRecordButton from './components/FloatingRecordButton';
import RecordingPanel from './components/RecordingPanel';
import { v4 as uuidv4 } from 'uuid';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { initializeAudioRecorder } from './utils';
import { identifyUser, trackLogin, trackSignUp, trackFirebaseNoteCreated } from './analytics';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { userService } from './services/UserService';
import logger from './utils/logger';
import { useTranslation } from 'react-i18next';
import FinishSignIn from './components/FinishSignIn';
import EmailLinkBridge from './components/EmailLinkBridge';
import { RecordingContextProvider, useRecordingContext } from './contexts/RecordingContext';
import RateLimiter from './utils/rateLimiter';
import {
  Bars3Icon,
  InboxIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  FolderIcon,
  UserCircleIcon,
  UserGroupIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

function AppContent() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [folders, setFolders] = useState([]);
  const location = useLocation();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [selectedSearchNote, setSelectedSearchNote] = useState(null);
  const { recordingContext, clearAllContext } = useRecordingContext();
  
  // Security: Rate limiter for note creation
  // 20 notes per hour = reasonable limit for normal users
  const createNoteRateLimiter = React.useMemo(
    () => new RateLimiter('createNote', 20, 60 * 60 * 1000),
    []
  );

  useEffect(() => {
    logger.log("App.js useEffect");
    let authUnsubscribe = null;
    let userSnapshotUnsubscribe = null;

    const initApp = async () => {
      await initializeAudioRecorder();

      authUnsubscribe = onAuthStateChanged(auth, (currentUser) => {
        // Cleanup previous user snapshot listener
        if (userSnapshotUnsubscribe) {
          userSnapshotUnsubscribe();
          userSnapshotUnsubscribe = null;
        }

        if (currentUser) {
          // Set user immediately to avoid login flash
          setUser(currentUser);
          const cachedUserData = userService.getCachedUserData(currentUser.uid);
          if (cachedUserData) {
            userService.setUser(currentUser, cachedUserData, { persistCache: false });
          }

          try {
            // Setup real-time listener for user data
            userSnapshotUnsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (userDoc) => {
              const userData = userDoc.exists() ? userDoc.data() : null;
              
              // Update global user service
              userService.setUser(currentUser, userData);
            }, (error) => {
              console.error('Error getting user data:', error);
            });

            // Analytics and initialization logic
            const hasVisitedBefore = localStorage.getItem(`hasVisited_${currentUser.uid}`);
            
            // Run analytics tracking
            // We use a check to ensure we don't track excessively, but onAuthStateChanged 
            // typically runs once on mount if logged in.
            identifyUser(currentUser.uid, getUserProperties(currentUser));
            
            if (!hasVisitedBefore) {
              localStorage.setItem(`hasVisited_${currentUser.uid}`, 'true');
              trackSignUp(currentUser.providerData[0]?.providerId || 'unknown', currentUser.uid);
            } else {
              trackLogin(currentUser.providerData[0]?.providerId || 'unknown', currentUser.uid);
            }
          } catch (error) {
            console.error('Error initializing user:', error);
          }
        } else {
          userService.clearUser();
          setUser(null);
        }
        setLoading(false);
      });
    };

    initApp();

    // Cleanup function
    return () => {
      if (authUnsubscribe) authUnsubscribe();
      if (userSnapshotUnsubscribe) userSnapshotUnsubscribe();
    };
  }, []);

  // Subscribe to userService for folders
  useEffect(() => {
    const updateFolders = (userData) => {
      if (userData && userData.folders) {
        setFolders(userData.folders);
      } else {
        setFolders([]);
      }
    };

    // Initial load
    updateFolders(userService.currentUser);

    // Subscribe to changes
    const unsubscribe = userService.subscribe(updateFolders);

    return unsubscribe;
  }, []);

  // Keyboard shortcut ⌘K (Cmd+K on Mac, Ctrl+K on Windows/Linux) to open search
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K en Mac o Ctrl+K en Windows/Linux
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchModalOpen(true);
      }
    };

    if (user) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [user]);

  // Function to get the header title based on current route
  const getHeaderTitle = () => {
    const path = location.pathname;
    
    if (path === '/inbox' || path === '/notes' || path === '/') {
      return t('notes.inbox');
    } else if (path === '/all') {
      return t('notes.allNotes');
    } else if (path === '/archive') {
      return t('notes.archive');
    } else if (path === '/gallery') {
      return t('gallery.title');
    } else if (path === '/profile') {
      return t('userProfile.profile');
    } else if (path.startsWith('/contacts')) {
      return t('contacts.contacts');
    } else if (path.startsWith('/folder/')) {
      const folderId = path.split('/folder/')[1];
      const folder = folders.find(f => f.id === folderId);
      return folder ? folder.name : t('common.folder');
    }
    
    return 'NoteMail';
  };

  // Function to get the header icon based on current route
  const getHeaderIcon = () => {
    const path = location.pathname;
    
    if (path === '/inbox' || path === '/notes' || path === '/') {
      return <InboxIcon className="h-6 w-6 text-orange-500" aria-hidden="true" />;
    } else if (path === '/all') {
      return <DocumentTextIcon className="h-6 w-6 text-orange-500" aria-hidden="true" />;
    } else if (path === '/archive') {
      return <ArchiveBoxIcon className="h-6 w-6 text-orange-500" aria-hidden="true" />;
    } else if (path === '/gallery') {
      return <PhotoIcon className="h-6 w-6 text-orange-500" aria-hidden="true" />;
    } else if (path === '/profile') {
      return <UserCircleIcon className="h-6 w-6 text-orange-500" aria-hidden="true" />;
    } else if (path.startsWith('/contacts')) {
      return <UserGroupIcon className="h-6 w-6 text-orange-500" aria-hidden="true" />;
    } else if (path.startsWith('/folder/')) {
      return <FolderIcon className="h-6 w-6 text-orange-500" aria-hidden="true" />;
    }
    
    return null;
  };

  // Function to get user properties for Mixpanel
  const getUserProperties = (user) => {
    return {
      "$name": user.displayName || "unknown",
      "$email": user.email || "unknown",
      "userLocale": navigator.language || "unknown",
      "appInterfaceLanguageLocale": navigator.language || "unknown",
      "isAdmin": false, // You might want to set this based on your app's logic
      "sendEmailAddress": user.email || "unknown",
      "signInProvider": user.providerData[0]?.providerId || "unknown",
      "userPhotoSet": !!user.photoURL,
      "countKeywords": 0, // You might want to set this based on your app's data
      "hasAboutMe": false // You might want to set this based on your app's data
    };
  };

  const handleOpenRecording = () => {
    setIsRecording(true);
  };

  const handleCloseRecording = (wasCancelled) => {
    setIsRecording(false);
    if (wasCancelled) {
      logger.log('Recording was cancelled');
      // No hacemos nada más, ya que la grabación fue cancelada
    }
  };

  const handleOpenSearchModal = () => {
    setIsSearchModalOpen(true);
  };

  const handleCloseSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  const handleSelectSearchNote = (note) => {
    setSelectedSearchNote(note);
  };

  const handleCloseNoteDetail = () => {
    setSelectedSearchNote(null);
  };

  const handleDeleteNote = (noteId) => {
    // Close the note detail if the deleted note was selected
    if (selectedSearchNote && selectedSearchNote.id === noteId) {
      setSelectedSearchNote(null);
    }
  };

  const handleSendRecording = async (audioBlob, durationSeconds, selectedEmail = null) => {
    try {
      if (!user) {
        logger.log('Usuario no autenticado');
        toast.error('Usuario no autenticado.');
        return;
      }

      // Security: Check rate limit before proceeding
      if (!createNoteRateLimiter.canProceed()) {
        const statusMessage = createNoteRateLimiter.getStatusMessage();
        logger.warn('Rate limit exceeded for note creation');
        toast.error(`${t('errors.rateLimitExceeded')}: ${statusMessage}`);
        return;
      }

      logger.log('Iniciando proceso de envío de grabación');
      logger.log('Email seleccionado:', selectedEmail || 'primary email');

      // Generar un UID único para el archivo
      const audioUID = uuidv4();
      logger.log('UID generado:', audioUID);

      // Subir el archivo a Firebase Storage
      logger.log('Iniciando subida del archivo...');
      const snapshot = await uploadBytes(storageRef(storage, `voicenotes/${user.uid}/${audioUID}.wav`), audioBlob);
      logger.log('Archivo subido exitosamente');

      const downloadURL = await getDownloadURL(snapshot.ref);
      logger.sensitive('URL de descarga obtenida:', downloadURL);

      // Preparar los datos de la nota
      const noteData = {
        uid: user.uid,
        title: "New voice note",
        transcript: "",
        summary: "",
        recordingURL: downloadURL,
        isPending2Process: true,
        recordingSizeKb: Math.round(audioBlob.size / 1024),
        recordingDuration: durationSeconds,
        deviceRecordingURL: "", // No aplica en web
        isEmailSent: false,
        isTodoCompleted: false,
        isProcessing: true,
        processingStatus: 10, // Trascribiendo
        processingProgress: 10,
        processingLastError: "",
        isPendingtoDelete: false,
        //noteType: 0, avoid setting this value, it will be set by the server
        insights: "",
        calendarTitle: "",
        calendarIsAllDay: false,
        hasLinks: false,
        extId: audioUID,
        shared: false,
        sharedViewed: false,
        isPinned: false,
        source: "webapp",
      };

      // Si hay un contacto en el contexto, añadirlo a la nota
      if (recordingContext.contact) {
        const contact = recordingContext.contact;
        const contactName = contact.type === 'person' && contact.surname 
          ? `${contact.name} ${contact.surname}`
          : contact.name;
        
        noteData.contacts = [{
          id: contact.id,
          contactName: contactName,
          lastNameUpdateDate: Math.floor(Date.now() / 1000)
        }];
        noteData.contactIds = [contact.id];
        
        logger.sensitive('Contacto añadido a la nota:', contactName);
      }
      
      logger.log('Datos de la nota preparados');

      // Llamar a la función de Firebase para procesar la nota
      logger.log('Llamando a la función processNote...');
      const processNote = httpsCallable(functions, 'processNote');
      const processNoteParams = {
        docId: "create_new",
        recordingURL: downloadURL,
        processTranscript: true,
        processSummary: true,
        processMetadata: true,
        processSendEmail: true,
        noteData: noteData,
      };
      
      // Add secondarySendEmail if a specific email was selected
      if (selectedEmail) {
        processNoteParams.secondarySendEmail = selectedEmail;
      }
      
      const result = await processNote(processNoteParams);

      logger.sensitive('Respuesta de processNote recibida:', result);

      // Modificar esta parte
      if (result.data && (result.data.result === 'Ok' || result.data.message === 'Note processed successfully')) {
        logger.log('Nota procesada exitosamente');
        
        // Security: Record successful note creation for rate limiting
        createNoteRateLimiter.recordCall();
        
        // Trackear la creación de la nota
        trackFirebaseNoteCreated(audioUID, durationSeconds);
        
        // Limpiar el contexto después de crear la nota exitosamente
        clearAllContext();
        
        //toast.success('Nota de voz enviada correctamente.');
      } else {
        logger.error('Error en el procesamiento de la nota:', result.data);
        toast.error('Error al procesar la nota de voz.');
      }
    } catch (error) {
      logger.error('Error detallado al enviar la nota de voz:', error);
      toast.error('Hubo un error al enviar la nota de voz.');
    } finally {
      logger.log('Proceso de envío de grabación finalizado');
      setIsRecording(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center">
        <div className="text-gray-400 text-base">Loading...</div>
      </div>
    );
  }

  // Ventana de nota aislada (doble clic): solo la nota, sin sidebar ni menú
  const isNotePopup = location.pathname.startsWith('/note/');
  if (isNotePopup) {
    if (!user) return <Navigate to="/login" replace />;
    return (
      <>
        <Routes>
          <Route path="/note/:noteId" element={<NoteDetailWindow />} />
        </Routes>
        <ToastContainer
          position="bottom-center"
          autoClose={3000}
          hideProgressBar={true}
          className="custom-toast-container"
          toastClassName="custom-toast"
          theme="dark"
        />
      </>
    );
  }

  return (
    <div className="app-container min-h-screen bg-[#F8FAFB]">
      {user && (
        <Sidebar 
          user={user} 
          mobileMenuOpen={mobileMenuOpen} 
          setMobileMenuOpen={setMobileMenuOpen}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          onOpenSearch={handleOpenSearchModal}
        />
      )}

      {/* Mobile Header */}
      {user && (
        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 md:hidden">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="flex-1 flex items-center gap-x-3">
            {getHeaderIcon()}
            <span className="text-2xl font-semibold text-gray-900">{getHeaderTitle()}</span>
          </div>
        </div>
      )}

      {/* Desktop Open Sidebar Button (when sidebar is closed) */}
      {user && !isSidebarOpen && (
        <div className="hidden md:fixed md:top-4 md:left-4 md:z-50 md:block">
           <button
             onClick={() => setIsSidebarOpen(true)}
             className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
             title="Open sidebar"
           >
             <span className="sr-only">Open sidebar</span>
             <Bars3Icon className="h-6 w-6" aria-hidden="true" />
           </button>
        </div>
      )}

      <main className={`app-main w-full transition-all duration-300 ease-in-out ${user && isSidebarOpen ? 'md:pl-64' : 'md:pl-0'}`}>
        <div className="w-full px-4 md:px-4 py-2 md:pt-2 md:pb-6">
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/inbox" /> : <Login />} />
            <Route
              path="/profile"
              element={user ? <UserProfile user={user} /> : <Navigate to="/login" />}
            />
            {/* Main Views */}
            <Route
              path="/inbox"
              element={user ? <Notes /> : <Navigate to="/login" />}
            />
            <Route
              path="/all"
              element={user ? <Notes /> : <Navigate to="/login" />}
            />
            <Route
              path="/archive"
              element={user ? <Notes /> : <Navigate to="/login" />}
            />
            <Route
              path="/folder/:folderId"
              element={user ? <Notes /> : <Navigate to="/login" />}
            />
            <Route
              path="/gallery"
              element={user ? <GalleryPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/contacts"
              element={user ? <ContactsList /> : <Navigate to="/login" />}
            />
            <Route
              path="/contacts/:contactId"
              element={user ? <ContactDetail /> : <Navigate to="/login" />}
            />
            {/* Helper Routes */}
            <Route path="/finishSignIn" element={<FinishSignIn />} />
            <Route path="/email-link-bridge" element={<EmailLinkBridge />} />
            <Route path="/email-link-sign-in" element={<EmailLinkBridge />} />
            <Route path="/public/note/:noteId" element={<NotePlayerPublicLink />} />
            <Route path="*" element={user ? <Navigate to="/inbox" /> : <Navigate to="/login" />} />
          </Routes>
        </div>
      </main>

      {user && (
        <>
          <FloatingRecordButton 
             onClick={handleOpenRecording} 
             isRecording={isRecording}
          />
          {isRecording && (
            <RecordingPanel 
               isOpen={isRecording} 
               onClose={handleCloseRecording}
               onSend={handleSendRecording}
            />
          )}
        </>
      )}

      {user && (
        <SearchModal 
          isOpen={isSearchModalOpen}
          onClose={handleCloseSearchModal}
          onSelectNote={handleSelectSearchNote}
          user={user}
        />
      )}

      {selectedSearchNote && (
        <NoteDetail 
          note={selectedSearchNote}
          onClose={handleCloseNoteDetail}
          onDelete={handleDeleteNote}
        />
      )}

      <ToastContainer 
        position="bottom-center"
        autoClose={3000}
        hideProgressBar={true}
        className="custom-toast-container"
        toastClassName="custom-toast"
        theme="dark"
      />
    </div>
  );
}

function App() {
  return (
    <RecordingContextProvider>
      <AppContent />
    </RecordingContextProvider>
  );
}

export default App;
